import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { FormButton, CancelButton } from "../../../components/Botton";
import Loader from "../../../components/Loader";
import useMasterUsers from "../useMasterUser";
import { IUserFormData, userValidationRules } from "../user.types";

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserFormDataWithPassword extends IUserFormData {
  password: string;
  confirmPassword: string;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose }) => {
  const { addUser, loading } = useMasterUsers();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<UserFormDataWithPassword>();

  const password = watch("password");

  const onSubmit = async (data: UserFormDataWithPassword) => {
    try {
      // Map phoneNumber to mobileNo as per your schema
      const userData = {
        name: data.name,
        mobileNo: data.phoneNumber,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
      };
      
      await addUser(userData);
      reset();
      onClose();
    } catch (error) {
      console.error("Error adding user:", error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="border-b-2 pb-4 mb-6">
          <h2 className="text-2xl font-semibold text-gray-700">Add New User</h2>
          <p className="text-sm text-gray-500 mt-1">
            Enter basic details. Role will be assigned automatically.
          </p>
        </div>

        {loading && <Loader />}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name Field */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register("name", userValidationRules.name)}
              className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
              placeholder="Enter full name"
            />
            {errors.name && (
              <span className="text-red-500 text-xs italic">
                {errors.name.message}
              </span>
            )}
          </div>

          {/* Phone Number Field */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              {...register("phoneNumber", userValidationRules.phoneNumber)}
              className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
              placeholder="Enter 10-digit phone number"
              maxLength={10}
            />
            {errors.phoneNumber && (
              <span className="text-red-500 text-xs italic">
                {errors.phoneNumber.message}
              </span>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              {...register("email", userValidationRules.email)}
              className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
              placeholder="Enter email address"
            />
            {errors.email && (
              <span className="text-red-500 text-xs italic">
                {errors.email.message}
              </span>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                    message: "Password must contain uppercase, lowercase, number and special character",
                  },
                })}
                className="appearance-none border rounded w-full py-2 px-3 pr-10 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
            {errors.password && (
              <span className="text-red-500 text-xs italic">
                {errors.password.message}
              </span>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
                className="appearance-none border rounded w-full py-2 px-3 pr-10 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
                placeholder="Confirm password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
              >
                {showConfirmPassword ? "🙈" : "👁️"}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="text-red-500 text-xs italic">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> User role will be assigned automatically by the system. 
              User can update other profile details after first login.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <CancelButton
              type="button"
              onClick={handleClose}
              className="py-2 px-6 rounded"
              children="Cancel"
            />
            <FormButton
              type="submit"
            //   disabled={loading}
              className="py-2 px-6 rounded"
              children={loading ? "Adding..." : "Add User"}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;